// Keeps consent checkboxes inside #cart-payment checked (or unchecked) across an async
// re-render (ENG-35182). checkout_update re-renders that section from whichever request's own
// form snapshot resolves last, so a request already in flight when the buyer toggles a box can
// land afterwards and silently overwrite the live DOM.
//
// `terms` (`purchase[terms]`), `wants_marketing` (`purchase[wants_marketing]`), `gdpr_consent`
// (`product_#{id}[gdpr_consent]`), and `email_tracking_opt_in` all qualify: each is keyed by a stable
// identity (the purchase, one specific product id, or — for email tracking — a single bare param with
// no scoping to begin with), so a name match always refers to the same real field on both sides of a
// re-render.
//
// `wants_marketing` and `email_tracking_opt_in` look immune at a glance because they render with an
// explicit `checked:`, but both read request-scoped state: `checkout_optin_checked?` reads
// `purchase.wants_marketing`, which checkout_update assigns from that request's own params before
// rendering, and `email_tracking_opt_in_checked?` reads `params[:email_tracking_opt_in]` directly. A
// stale response therefore renders the old choice for both, exactly like `terms`.
//
// Contract-signature checkboxes (`entrant[contract_signatures][N][agreed]`) are deliberately
// excluded — N is a positional array index that can point at a different contract when the
// purchase/order-bump set differs between two racing responses, so restoring by name there could
// tick the wrong contract.
export function isPreservableCheckboxName(name) {
  return (
    name === 'purchase[terms]' ||
    name === 'purchase[wants_marketing]' ||
    name === 'email_tracking_opt_in' ||
    name.endsWith('[gdpr_consent]')
  )
}

// Name -> checked for each preservable box on the page before the swap.
export function checkedStatesIn(checkboxes) {
  return new Map(
    checkboxes
      .filter((el) => isPreservableCheckboxName(el.name))
      .map((el) => [el.name, el.checked])
  )
}

// Applies the pre-swap state, in both directions, to every preservable box that was on the page
// before the swap. The pre-swap DOM reflects the buyer's latest real interaction, so it wins over
// whatever the just-arrived HTML says — a one-directional restore (force-true only) would silently
// re-check a box the buyer had since unchecked. A box that first appears in the response (a new
// order bump's GDPR text, the email tracking box after a country change) was never seen by the
// buyer, so it keeps the state the server rendered.
export function restoreChecked(checkboxes, checkedStates) {
  checkboxes
    .filter(
      (el) => isPreservableCheckboxName(el.name) && checkedStates.has(el.name)
    )
    .forEach((el) => {
      el.checked = checkedStates.get(el.name)
    })
}

export function cartPaymentCheckboxes(container) {
  return container
    ? Array.from(container.querySelectorAll('input[type=checkbox]'))
    : []
}
